/**
 * LocalModal - Modal for adding/editing counting locations
 *
 * Story 2.34: agora suporta N contatos EXTRAS por local (alem do principal
 * inline em responsavel/telefone/ramal/email).
 */
import React, { useState } from 'react';
import type { LocalContagem } from '../../hooks/useHospitalData';
import { formatTelefone } from '../../hooks/useHospitalForm';
import type { ContatoExtraDraft } from '../../hooks/useHospitalLocais';

interface NewLocalState {
  local: string;
  responsavel: string;
  telefone: string;
  ramal: string;
  email: string;
}

interface LocalModalProps {
  show: boolean;
  editingLocal: LocalContagem | null;
  newLocal: NewLocalState;
  savingLocal: boolean;
  onUpdateField: (field: keyof NewLocalState, value: string) => void;
  onSave: () => void;
  onClose: () => void;

  // Story 2.34: contatos extras
  contatosExtras?: ContatoExtraDraft[];
  onAddContatoExtra?: () => void;
  onUpdateContatoExtraField?: (index: number, field: keyof ContatoExtraDraft, value: string | number | boolean) => void;
  onRemoveContatoExtra?: (index: number) => void;
}

export const LocalModal: React.FC<LocalModalProps> = ({
  show,
  editingLocal,
  newLocal,
  savingLocal,
  onUpdateField,
  onSave,
  onClose,
  contatosExtras = [],
  onAddContatoExtra,
  onUpdateContatoExtraField,
  onRemoveContatoExtra,
}) => {
  // Story 2.34: secao "Contatos Extras" dobravel
  const [extrasExpanded, setExtrasExpanded] = useState(false);
  const visiveis = contatosExtras.filter(c => !c._toDelete);
  const hasExtras = visiveis.length > 0;

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {editingLocal ? 'Editar' : 'Adicionar'} Local de Contagem
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Preencha as informações do local de contagem
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <div className="p-6 space-y-4">
          {/* Local da Contagem */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Local da Contagem <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="Ex: Almoxarifado Central"
              value={newLocal.local}
              onChange={(e) => onUpdateField('local', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              autoFocus
            />
          </div>

          {/* Responsável */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Responsável <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="Nome do responsável"
              value={newLocal.responsavel}
              onChange={(e) => onUpdateField('responsavel', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Telefone + Ramal */}
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Telefone
              </label>
              <input
                type="text"
                placeholder="(00) 00000-0000"
                value={newLocal.telefone}
                onChange={(e) => onUpdateField('telefone', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                maxLength={15}
              />
            </div>

            <div className="col-span-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Ramal
              </label>
              <input
                type="text"
                placeholder="0000"
                value={newLocal.ramal}
                onChange={(e) => onUpdateField('ramal', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                maxLength={6}
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Email
            </label>
            <input
              type="email"
              placeholder="email@exemplo.com"
              value={newLocal.email}
              onChange={(e) => onUpdateField('email', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                newLocal.email && !newLocal.email.includes('@')
                  ? 'border-red-500 dark:border-red-500'
                  : 'border-gray-300 dark:border-gray-600'
              }`}
            />
            {newLocal.email && !newLocal.email.includes('@') && (
              <p className="text-xs text-red-500 mt-1">Email deve conter @</p>
            )}
          </div>

          {/* Story 2.34: secao dobravel "Contatos Extras" */}
          {onAddContatoExtra && (
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-2">
              <button
                type="button"
                onClick={() => setExtrasExpanded(prev => !prev)}
                className="w-full flex items-center justify-between text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 mb-2"
              >
                <span className="flex items-center gap-2">
                  <svg
                    className={`w-4 h-4 transition-transform ${extrasExpanded ? 'rotate-90' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  Contatos Extras {hasExtras && <span className="text-xs text-blue-600 dark:text-blue-400">({visiveis.length})</span>}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {extrasExpanded ? 'Recolher' : 'Expandir'}
                </span>
              </button>

              {extrasExpanded && (
                /* #7: lista rolável — antes a área não rolava e os contatos
                   além de ~3 ficavam cortados/inacessíveis. */
                <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                  {visiveis.length === 0 && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                      Nenhum contato extra cadastrado. Clique em "+ Adicionar" para incluir um contato adicional ao local.
                    </p>
                  )}

                  {contatosExtras.map((contato, idx) => {
                    if (contato._toDelete) return null;
                    return (
                      <div
                        key={contato.id ?? `new-${idx}`}
                        className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-gray-50 dark:bg-gray-900/30"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                            Contato Extra {idx + 1}
                          </span>
                          <button
                            type="button"
                            onClick={() => onRemoveContatoExtra?.(idx)}
                            className="text-red-500 hover:text-red-700 dark:hover:text-red-400 text-xs"
                            disabled={savingLocal}
                          >
                            Remover
                          </button>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <input
                            type="text"
                            placeholder="Nome *"
                            value={contato.nome}
                            onChange={(e) => onUpdateContatoExtraField?.(idx, 'nome', e.target.value)}
                            className="col-span-2 px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          />
                          <input
                            type="text"
                            placeholder="Cargo (opcional)"
                            value={contato.cargo}
                            onChange={(e) => onUpdateContatoExtraField?.(idx, 'cargo', e.target.value)}
                            className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          />
                          <input
                            type="text"
                            placeholder="Telefone"
                            value={contato.telefone}
                            onChange={(e) => onUpdateContatoExtraField?.(idx, 'telefone', e.target.value)}
                            className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            maxLength={15}
                          />
                          <input
                            type="text"
                            placeholder="Ramal"
                            value={contato.ramal}
                            onChange={(e) => onUpdateContatoExtraField?.(idx, 'ramal', e.target.value.replace(/\D/g, '').slice(0, 6))}
                            className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            maxLength={6}
                          />
                          <input
                            type="email"
                            placeholder="Email"
                            value={contato.email}
                            onChange={(e) => onUpdateContatoExtraField?.(idx, 'email', e.target.value)}
                            className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          />
                        </div>
                      </div>
                    );
                  })}

                  <button
                    type="button"
                    onClick={onAddContatoExtra}
                    className="w-full px-3 py-2 text-sm border border-dashed border-blue-400 dark:border-blue-600 rounded-lg text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                    disabled={savingLocal}
                  >
                    + Adicionar Contato Extra
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            disabled={savingLocal}
            className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancelar
          </button>
          <button
            onClick={onSave}
            disabled={savingLocal}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {savingLocal ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
};
